import {
    CoreHomeCareLeadValidationField,
    CoreHomeCareLeadValidationFieldBridgePathsFromHomeCareLeadEntity,
    findRelatedField,
    ValidationFieldSource,
    ValidationOperation,
    ValidationRuleParams,
    ValidationRuleSeverity,
    ValidationRuleType,
} from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { DeleteButton } from "components/ui/action-button";
import { fancyAlert } from "components/ui/fancy-alert";
import { InlineBanner } from "components/ui/inline-banner";
import { SecondaryButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import { Spinner } from "components/ui/spinner";
import { AdvancedSearchEntityType } from "generated/graphql";
import React, { useEffect, useState } from "react";
import { useBridgeEntityType, useBridgeQuestions } from "store/selectors/bridge";

const ruleRowGridColumns = "grid-cols-[14rem_minmax(12rem,1fr)_12rem_2.5rem]";

interface RuleDraft {
    fieldSource: ValidationFieldSource;
    fieldKey: string;
    severity: ValidationRuleSeverity;
    ruleType: ValidationRuleType;
    params: ValidationRuleParams;
}

interface FieldOption {
    value: string;
    label: string;
}

interface Props {
    operation: ValidationOperation;
    title: string;
    description: string;
}

export const ValidationRuleSetEditor: React.FC<Props> = ({
    operation,
    title,
    description,
}) => {
    const trpcClient = trpc.useTRPC();
    const intakeQuestions = useBridgeQuestions(AdvancedSearchEntityType.HomeCareLead);
    const intakeFieldOptions: FieldOption[] = intakeQuestions
        .filter(q => !!q.key)
        .map(q => ({ value: q.key, label: q.title ?? q.key }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const { entityType: homeCareLeadType, allTypes: bridgeTypes } = useBridgeEntityType(
        AdvancedSearchEntityType.HomeCareLead,
    );
    const coreFieldOptions: FieldOption[] = Object.values(CoreHomeCareLeadValidationField)
        .map(value => {
            const path =
                CoreHomeCareLeadValidationFieldBridgePathsFromHomeCareLeadEntity[value];
            const label = homeCareLeadType
                ? (findRelatedField(path, homeCareLeadType, bridgeTypes)?.title ?? path)
                : path;
            return { value, label };
        })
        .sort((a, b) => a.label.localeCompare(b.label));

    const listQuery = useQuery({
        ...trpcClient.validationRule.listForOperation.queryOptions({ operation }),
    });
    const setMutation = useMutation({
        ...trpcClient.validationRule.setForOperation.mutationOptions(),
    });

    const [drafts, setDrafts] = useState<RuleDraft[] | null>(null);

    useEffect(() => {
        if (listQuery.data && drafts === null) {
            setDrafts(
                listQuery.data.map(r => ({
                    fieldSource: r.fieldSource,
                    fieldKey: r.fieldKey,
                    severity: r.severity,
                    ruleType: r.ruleType,
                    params: r.params,
                })),
            );
        }
    }, [listQuery.data, drafts]);

    if (listQuery.isLoading || drafts === null) {
        return <Spinner />;
    }
    if (listQuery.isError) {
        return (
            <InlineBanner type="error">
                Could not load validation rules. Try refreshing the page.
            </InlineBanner>
        );
    }

    const optionsFor = (fieldSource: ValidationFieldSource): FieldOption[] =>
        fieldSource === ValidationFieldSource.CoreHomeCareLeadField
            ? coreFieldOptions
            : intakeFieldOptions;

    const usedFieldKeys = (fieldSource: ValidationFieldSource, excludeIndex: number) =>
        new Set(
            drafts
                .filter((d, i) => i !== excludeIndex && d.fieldSource === fieldSource)
                .map(d => d.fieldKey),
        );

    const availableOptionsFor = (
        fieldSource: ValidationFieldSource,
        excludeIndex: number,
    ): FieldOption[] => {
        const used = usedFieldKeys(fieldSource, excludeIndex);
        return optionsFor(fieldSource).filter(option => !used.has(option.value));
    };

    const firstUnusedField = (): {
        fieldSource: ValidationFieldSource;
        fieldKey: string;
    } | null => {
        for (const fieldSource of [
            ValidationFieldSource.CoreHomeCareLeadField,
            ValidationFieldSource.IntakeQuestion,
        ]) {
            const available = availableOptionsFor(fieldSource, drafts.length);
            if (available.length > 0) {
                return { fieldSource, fieldKey: available[0].value };
            }
        }
        return null;
    };

    const commit = async (nextDrafts: RuleDraft[]) => {
        const previousDrafts = drafts;
        setDrafts(nextDrafts);
        try {
            const saved = await setMutation.mutateAsync({
                operation,
                rules: nextDrafts.filter(d => d.fieldKey !== ""),
            });
            setDrafts(
                saved.map(r => ({
                    fieldSource: r.fieldSource,
                    fieldKey: r.fieldKey,
                    severity: r.severity,
                    ruleType: r.ruleType,
                    params: r.params,
                })),
            );
        } catch (error) {
            setDrafts(previousDrafts);
            await fancyAlert(
                "Could not save validation rule",
                error instanceof Error ? error.message : "Unable to save this change",
            );
        }
    };

    const updateDraft = (index: number, patch: Partial<RuleDraft>) =>
        commit(drafts.map((d, i) => (i === index ? { ...d, ...patch } : d)));

    const addDraft = () => {
        const next = firstUnusedField();
        if (!next) {
            return;
        }
        commit([
            ...drafts,
            {
                fieldSource: next.fieldSource,
                fieldKey: next.fieldKey,
                severity: ValidationRuleSeverity.Hard,
                ruleType: ValidationRuleType.Required,
                params: null,
            },
        ]);
    };

    const removeDraft = (index: number) => commit(drafts.filter((_, i) => i !== index));

    const handleFieldSourceChange = (
        index: number,
        fieldSource: ValidationFieldSource,
    ) => {
        updateDraft(index, {
            fieldSource,
            fieldKey: availableOptionsFor(fieldSource, index)[0]?.value ?? "",
        });
    };

    const canAddDraft = firstUnusedField() !== null;

    return (
        <div className="space-y-3">
            <div>
                <h3 className="font-semibold">{title}</h3>
                <p className="text-sm text-gray-500">{description}</p>
            </div>

            {drafts.length === 0 ? null : (
                <div className="overflow-hidden rounded-lg border border-gray-200">
                    <div
                        className={`grid ${ruleRowGridColumns} gap-3 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-500`}>
                        <div>Field type</div>
                        <div>Field</div>
                        <div>Severity</div>
                        <div />
                    </div>
                    <div className="divide-y divide-gray-100">
                        {drafts.map((draft, index) => (
                            <div
                                key={index}
                                className={`grid ${ruleRowGridColumns} items-center gap-3 p-3`}>
                                <Select
                                    aria-label="Field type"
                                    value={draft.fieldSource}
                                    onChange={e =>
                                        handleFieldSourceChange(
                                            index,
                                            e.target.value as ValidationFieldSource,
                                        )
                                    }>
                                    <option
                                        value={
                                            ValidationFieldSource.CoreHomeCareLeadField
                                        }
                                        disabled={
                                            availableOptionsFor(
                                                ValidationFieldSource.CoreHomeCareLeadField,
                                                index,
                                            ).length === 0
                                        }>
                                        Home care client field
                                    </option>
                                    <option
                                        value={ValidationFieldSource.IntakeQuestion}
                                        disabled={
                                            availableOptionsFor(
                                                ValidationFieldSource.IntakeQuestion,
                                                index,
                                            ).length === 0
                                        }>
                                        Intake question
                                    </option>
                                </Select>
                                <Select
                                    aria-label="Field"
                                    value={draft.fieldKey}
                                    onChange={e =>
                                        updateDraft(index, {
                                            fieldKey: e.target.value,
                                        })
                                    }>
                                    {availableOptionsFor(draft.fieldSource, index).map(
                                        option => (
                                            <option
                                                key={option.value}
                                                value={option.value}>
                                                {option.label}
                                            </option>
                                        ),
                                    )}
                                </Select>
                                <Select
                                    aria-label="Severity"
                                    value={draft.severity}
                                    onChange={e =>
                                        updateDraft(index, {
                                            severity: e.target
                                                .value as ValidationRuleSeverity,
                                        })
                                    }>
                                    <option value={ValidationRuleSeverity.Hard}>
                                        Blocks the action
                                    </option>
                                    <option value={ValidationRuleSeverity.Warning}>
                                        Warns before the action
                                    </option>
                                </Select>
                                <DeleteButton
                                    backgroundColor="bg-white"
                                    size="sm"
                                    confirm={{
                                        title: "Delete rule",
                                        message: `Are you sure you want to delete the "${
                                            optionsFor(draft.fieldSource).find(
                                                option => option.value === draft.fieldKey,
                                            )?.label ?? draft.fieldKey
                                        }" rule?`,
                                    }}
                                    onClick={() => removeDraft(index)}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <SecondaryButton type="button" disabled={!canAddDraft} onClick={addDraft}>
                <span className="text-sm">Add rule</span>
            </SecondaryButton>
        </div>
    );
};
